import { useState, useEffect } from 'react'
import { BarChart3, TrendingUp, Users, MessageSquare, CheckCircle, AlertCircle, Mail, MessageCircle, Bell } from 'lucide-react'
import { announcementApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import PageHeader from '../components/common/PageHeader'
import LoadingSpinner from '../components/common/LoadingSpinner'

const AnnouncementStats = () => {
  const { selectedBuildingId, selectedBuildingName } = useAuth()
  const [globalStats, setGlobalStats] = useState(null)
  const [selectedBuildingStats, setSelectedBuildingStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [buildingStatsLoading, setBuildingStatsLoading] = useState(false)

  useEffect(() => {
    loadGlobalStats()
  }, [])

  useEffect(() => {
    if (selectedBuildingId) {
      loadBuildingStats(selectedBuildingId)
    } else {
      setSelectedBuildingStats(null)
    }
  }, [selectedBuildingId])

  const loadGlobalStats = async () => {
    try {
      setLoading(true)
      const response = await announcementApi.getGlobalStats()
      setGlobalStats(response.data)
    } catch (error) {
      console.error('Error loading global stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadBuildingStats = async (buildingId) => {
    try {
      setBuildingStatsLoading(true)
      const response = await announcementApi.getBuildingStats(buildingId)
      setSelectedBuildingStats(response.data)
    } catch (error) {
      console.error('Error loading building stats:', error)
      setSelectedBuildingStats(null)
    } finally {
      setBuildingStatsLoading(false)
    }
  }

  const StatCard = ({ title, value, icon: Icon, color = 'indigo', subtext, trend }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className={`text-lg font-medium text-${color}-900`}>
                {value !== null && value !== undefined ? value.toLocaleString() : 'N/A'}
              </dd>
              {subtext && (
                <dd className="text-sm text-gray-500">{subtext}</dd>
              )}
              {trend && (
                <dd className={`text-sm ${trend.positive ? 'text-green-600' : 'text-red-600'} flex items-center`}>
                  <TrendingUp className="h-4 w-4 mr-1" />
                  {trend.value}
                </dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )

  const NotificationMethodChart = ({ data, title }) => {
    if (!data || typeof data !== 'object') return null

    const methods = Object.entries(data)
    const total = methods.reduce((sum, [, count]) => sum + count, 0)
    
    if (total === 0) return null

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="space-y-4">
          {methods.map(([method, count]) => {
            const percentage = total > 0 ? (count / total) * 100 : 0
            const getIcon = () => {
              switch (method.toLowerCase()) {
                case 'email': return Mail
                case 'sms': return MessageCircle
                case 'pushnotification': return Bell
                default: return Bell
              }
            }
            const Icon = getIcon()

            return (
              <div key={method} className="flex items-center">
                <Icon className="h-5 w-5 text-gray-400 mr-3" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">{method}</span>
                    <span className="text-sm text-gray-500">{count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{percentage.toFixed(1)}%</div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Announcement Statistics"
        subtitle="Overview of announcement performance and engagement"
        action={
          <div className="flex items-center space-x-4">
            <BarChart3 className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-500">Real-time data</span>
          </div>
        }
      />

      {/* Global Statistics */}
      {globalStats && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Global Statistics</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Announcements"
              value={globalStats.totalAnnouncements}
              icon={MessageSquare}
              color="blue"
            />
            <StatCard
              title="Total Recipients"
              value={globalStats.totalRecipients}
              icon={Users}
              color="green"
            />
            <StatCard
              title="Total Read"
              value={globalStats.totalRead}
              icon={CheckCircle}
              color="indigo"
              subtext={globalStats.totalRecipients > 0 ? 
                `${((globalStats.totalRead / globalStats.totalRecipients) * 100).toFixed(1)}% read rate` : 
                undefined
              }
            />
            <StatCard
              title="Pending Acknowledgments"
              value={globalStats.pendingAcknowledgments}
              icon={AlertCircle}
              color="yellow"
            />
          </div>
          
          {globalStats.notificationMethodCounts && (
            <div className="mt-6">
              <NotificationMethodChart 
                data={globalStats.notificationMethodCounts} 
                title="Global Notification Method Distribution"
              />
            </div>
          )}
        </div>
      )}

      {/* Selected Building Stats */}
      {selectedBuildingId && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {selectedBuildingName} Statistics
          </h3>
          {buildingStatsLoading ? (
            <LoadingSpinner />
          ) : selectedBuildingStats ? (
            <div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  title="Total Announcements"
                  value={selectedBuildingStats.totalAnnouncements}
                  icon={MessageSquare}
                  color="blue"
                />
                <StatCard
                  title="Total Recipients"
                  value={selectedBuildingStats.totalRecipients}
                  icon={Users}
                  color="green"
                />
                <StatCard
                  title="Total Read"
                  value={selectedBuildingStats.totalRead}
                  icon={CheckCircle}
                  color="indigo"
                  subtext={selectedBuildingStats.totalRecipients > 0 ? 
                    `${((selectedBuildingStats.totalRead / selectedBuildingStats.totalRecipients) * 100).toFixed(1)}% read rate` : 
                    undefined
                  }
                />
                <StatCard
                  title="Pending Acknowledgments"
                  value={selectedBuildingStats.pendingAcknowledgments}
                  icon={AlertCircle}
                  color="yellow"
                />
              </div>
              
              {selectedBuildingStats.notificationMethodCounts && (
                <div className="mt-6">
                  <NotificationMethodChart 
                    data={selectedBuildingStats.notificationMethodCounts} 
                    title={selectedBuildingName ? `${selectedBuildingName} Notification Methods` : 'Notification Methods'}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
              <p className="mt-1 text-sm text-gray-500">Statistics for this building are not available.</p>
            </div>
          )}
        </div>
      )}

    </div>
  )
}

export default AnnouncementStats